import { useEffect, useRef, useState } from 'react'
import '../styles/purchase-orders.css'

export type PoBrand = { id: string; name: string; styleCount: number }

const MAX_BRAND_RESULTS = 50

// Type-to-filter brand picker shared by the Purchase Orders pages: the full list is loaded
// once, filtering is a case-insensitive substring match anywhere in the name. The dropdown
// may close on outside click (only dimming modals must not).
export function BrandCombobox({ brands, loading, value, onSelect }: {
    brands: PoBrand[]
    loading: boolean
    value: PoBrand | null
    onSelect: (brand: PoBrand | null) => void
}) {
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [highlight, setHighlight] = useState(0)
    const wrapRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onDown = (e: PointerEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', onDown)
        return () => document.removeEventListener('pointerdown', onDown)
    }, [])

    const q = query.trim().toLowerCase()
    const matches = q ? brands.filter(b => b.name.toLowerCase().includes(q)) : brands
    const shown = matches.slice(0, MAX_BRAND_RESULTS)

    const pick = (b: PoBrand) => {
        onSelect(b)
        setQuery('')
        setOpen(false)
    }

    return (
        <div className="po-combobox" ref={wrapRef}>
            <input
                className="form-input"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                disabled={loading}
                value={open ? query : (value?.name ?? '')}
                placeholder={loading ? 'Loading brands…' : (value?.name ?? 'Type to search brands…')}
                onFocus={() => { setQuery(''); setHighlight(0); setOpen(true) }}
                // Also open on click: after picking, the input keeps focus, so clicking it
                // again fires no focus event — without this the list couldn't reopen.
                onClick={() => { if (!open) { setQuery(''); setHighlight(0); setOpen(true) } }}
                onChange={e => { setQuery(e.target.value); setHighlight(0); setOpen(true) }}
                onKeyDown={e => {
                    if (!open) return
                    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, shown.length - 1)) }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)) }
                    else if (e.key === 'Enter') { e.preventDefault(); if (shown[highlight]) pick(shown[highlight]) }
                    else if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur() }
                }}
            />
            {value && !open && (
                <button type="button" className="po-combobox-clear" title="Clear brand" onClick={() => onSelect(null)}>✕</button>
            )}
            {open && !loading && (
                <div className="po-combobox-results" role="listbox">
                    {shown.length === 0 && (
                        <div className="po-combobox-empty">No brands match “{query.trim()}”.</div>
                    )}
                    {shown.map((b, i) => (
                        <button
                            type="button"
                            role="option"
                            aria-selected={i === highlight}
                            key={b.id}
                            className={`po-combobox-result${i === highlight ? ' highlighted' : ''}`}
                            onMouseEnter={() => setHighlight(i)}
                            onClick={() => pick(b)}
                        >
                            <strong>{b.name}</strong>
                            <span>{b.styleCount.toLocaleString()} styles</span>
                        </button>
                    ))}
                    {matches.length > shown.length && (
                        <div className="po-combobox-empty">Showing {shown.length} of {matches.length.toLocaleString()} — keep typing to narrow.</div>
                    )}
                </div>
            )}
        </div>
    )
}
